/**
 * Hard limits and capability probing.
 *
 * Browsers impose canvas area limits that are invisible until they bite (older
 * iOS Safari silently blanks canvases past ~16 Mpx; Chrome caps a single
 * dimension around 65 k px but memory pressure arrives far sooner). We therefore
 * enforce a conservative product limit and *probe* the device for headroom
 * instead of trusting a hard-coded number.
 */

/** Product limit for the MVP: a 4096x4096 document is the largest we promise. */
export const MAX_DIMENSION = 4096;
export const MAX_PIXELS = MAX_DIMENSION * MAX_DIMENSION;

/** Above this we warn before opening, because history snapshots get expensive. */
export const LARGE_IMAGE_PIXELS = 8_000_000;

/**
 * History is capped by both step count and a byte budget.
 *
 * The budget is what stops a 4096px document from keeping 60 snapshots of
 * itself alive; see `marginalLayerBytes` in `model/document.ts` for what gets
 * charged. 256 MB is deliberately below what a Chrome tab can usually hold: the
 * editor also needs room for the live document, decoded images and the engine's
 * own caches, and a tab that crashes takes the unsaved work with it.
 */
export const HISTORY_LIMIT = 60;
export const HISTORY_MEMORY_BUDGET = 256 * 1024 * 1024;

/**
 * The engine's decoded-image cache and mask-bake cache are bounded by the same
 * discipline as history: bytes, not entry counts (see `core/cacheBudget.ts`).
 *
 * The image budget is the backstop — the cache reconciles against the live
 * scene on every rebuild, so under normal use it holds only the current
 * layers' decodes. The bake budget is half that because a bake is rebakeable
 * by definition: it costs time, not user data. Both sit well below what a
 * Chrome tab survives, for the same reason the history budget does.
 */
export const IMAGE_CACHE_BUDGET = 256 * 1024 * 1024;
export const MASK_BAKE_CACHE_BUDGET = 128 * 1024 * 1024;
/** Composite group buffers and clip-run sheets (Iteration 14), same class of object. */
export const COMPOSITE_CACHE_BUDGET = 128 * 1024 * 1024;

export interface SizeCheck {
  ok: boolean;
  /** Set when the size is over the limit but a downscale would make it fit. */
  suggestedScale?: number;
  reason?: string;
}

export function checkDocumentSize(
  width: number,
  height: number,
  maxDimension = MAX_DIMENSION,
): SizeCheck {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    return { ok: false, reason: 'Image dimensions are not usable.' };
  }
  const longest = Math.max(width, height);
  if (longest <= maxDimension && width * height <= maxDimension * maxDimension) {
    return { ok: true };
  }
  const scale = Math.min(maxDimension / width, maxDimension / height);
  return {
    ok: false,
    suggestedScale: scale,
    reason: `${width}×${height} exceeds the ${maxDimension}px limit.`,
  };
}

/** Scale factor that brings an image inside the limit (1 when already fine). */
export function suggestedScale(width: number, height: number, maxDimension = MAX_DIMENSION) {
  const check = checkDocumentSize(width, height, maxDimension);
  return check.ok ? 1 : (check.suggestedScale ?? 1);
}

export function isLargeImage(width: number, height: number): boolean {
  return width * height > LARGE_IMAGE_PIXELS;
}

/** Human-readable byte count for the status bar and the History panel. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} kB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

let probedMax: number | null = null;

/**
 * Empirically find the largest square canvas this device can actually allocate.
 *
 * Uses a binary search between MAX_DIMENSION and a generous ceiling, testing that
 * the context exists AND that the browser kept the requested size. Cached for the
 * session because allocation is not free.
 */
export async function probeMaxDimension(
  createCanvas: () => HTMLCanvasElement | OffscreenCanvas = () => document.createElement('canvas'),
  ceiling = 16384,
): Promise<number> {
  if (probedMax !== null) return probedMax;

  const canAllocate = (size: number): boolean => {
    try {
      const canvas = createCanvas();
      canvas.width = size;
      canvas.height = size;
      if (canvas.width !== size || canvas.height !== size) return false;
      const ctx = canvas.getContext('2d');
      const ok = ctx !== null;
      // Help the GC reclaim the backing store immediately.
      canvas.width = 1;
      canvas.height = 1;
      return ok;
    } catch {
      return false;
    }
  };

  let lo = 512;
  let hi = ceiling;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (canAllocate(mid)) lo = mid;
    else hi = mid - 1;
  }
  probedMax = Math.max(512, lo);
  return probedMax;
}

/** Test helper: reset the memoised probe result. */
export function resetProbedMax(): void {
  probedMax = null;
}

/** Limit actually offered to the user: never below the product minimum. */
export function effectiveMaxDimension(probed: number): number {
  return Math.max(MAX_DIMENSION, probed);
}
